import React, { memo } from 'react';
import { Box, Typography, Chip, Stack } from '@mui/material';
import {
    Assignment,
    CalendarMonth,
    PlayCircle,
    Schedule
} from '@mui/icons-material';
import { ExamsData, Holiday, DashboardEvent } from '../../types/dashboardTypes';
import { getHashedColor } from '../../utils/colorUtils';
import UpcomingEventsCard, { getDaysUntil, QUICK_INFO_CARD_SX } from './UpcomingEventsCard';
import {
    AccentChip,
    DashboardCard,
    DashboardEmptyState,
    DashboardListCardSkeleton,
    DashboardListRow,
    DateTile,
    useDashboardAccent,
    useDashboardTones,
} from './DashboardCard';

interface QuickInfoCardsProps {
    exams: ExamsData;
    holidays: Holiday[];
    events?: DashboardEvent[];
    showExams?: boolean;      
    showHolidays?: boolean;
    showEvents?: boolean;
}

const QuickInfoCards: React.FC<QuickInfoCardsProps> = memo(({ 
    exams, 
    holidays,
    events = [],
    showExams = true,
    showHolidays = true,
    showEvents = true
}) => {
    const { isDark } = useDashboardTones();
    const running = useDashboardAccent('purple');
    const upcoming = useDashboardAccent('violet');

    const formatDate = (dateStr: string) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    const getHolidayColor = (type: string) => getHashedColor(type, isDark);
    const rowChipSx = { fontSize: 9, height: 20, fontWeight: 600 };

    return (
        <Box display="flex" gap={2.5} flexDirection={{ xs: 'column', lg: 'row' }} flexWrap="wrap">
            {/* Exams Card - only show if showExams is true */}
            {showExams && (
                <DashboardCard accent="purple" icon={Assignment} title="Exams" sx={QUICK_INFO_CARD_SX}>
                    {exams.active.length === 0 && exams.upcoming.length === 0 ? (
                        <DashboardEmptyState
                            accent="purple"
                            icon={Assignment}
                            title="Clear schedule ahead!"
                            description="No exams scheduled at this time"
                            sx={{ p: 1.5, minHeight: 110 }}
                        />
                    ) : (
                        <Box sx={{ p: 1.5, minHeight: 110 }}>
                            <Stack spacing={1}>
                                {/* Active Exams */}
                                {exams.active.map((exam) => (
                                    <DashboardListRow key={exam._id}>
                                        <PlayCircle sx={{ color: running.text, fontSize: 18 }} />
                                        <Box flex={1} minWidth={0}>
                                            <Typography variant="body2" fontWeight={500} noWrap fontSize={12}>
                                                {exam.name}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" fontSize={10}>
                                                {exam.classNames.slice(0, 2).join(', ')}
                                                {exam.classNames.length > 2 && ` +${exam.classNames.length - 2}`}
                                            </Typography>
                                        </Box>
                                        <AccentChip accent="purple" variant="solid" label="Running" sx={rowChipSx} />
                                    </DashboardListRow>
                                ))}

                                {/* Upcoming Exams */}
                                {exams.upcoming.slice(0, 2).map((exam) => (
                                    <DashboardListRow key={exam._id}>
                                        <Schedule sx={{ color: upcoming.text, fontSize: 18 }} />
                                        <Box flex={1} minWidth={0}>
                                            <Typography variant="body2" fontWeight={500} noWrap fontSize={12}>
                                                {exam.name}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" fontSize={10}>
                                                {formatDate(exam.startDate)} - {formatDate(exam.endDate)}
                                            </Typography>
                                        </Box>
                                        <AccentChip accent="violet" label={getDaysUntil(exam.startDate)} sx={rowChipSx} />
                                    </DashboardListRow>
                                ))}
                            </Stack>
                        </Box>
                    )}
                </DashboardCard>
            )}

            {/* Holidays Card - only show if showHolidays is true */}
            {showHolidays && (
                <DashboardCard accent="orange" icon={CalendarMonth} title="Upcoming Holidays" sx={QUICK_INFO_CARD_SX}>
                    {holidays.length === 0 ? (
                        <DashboardEmptyState
                            accent="orange"
                            icon={CalendarMonth}
                            title="All work, no breaks!"
                            description="No holidays in the next 30 days"
                            sx={{ p: 1.5, minHeight: 110 }}
                        />
                    ) : (
                        <Box sx={{ p: 1.5, minHeight: 110 }}>
                            <Stack spacing={1}>
                                {holidays.slice(0, 3).map((holiday) => {
                                    const colors = getHolidayColor(holiday.type);
                                    return (
                                        <DashboardListRow key={holiday._id}>
                                            <DateTile accent="orange" date={holiday.date} />
                                            <Box flex={1} minWidth={0}>
                                                <Typography variant="body2" fontWeight={500} noWrap fontSize={12}>
                                                    {holiday.name}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" fontSize={10}>
                                                    {getDaysUntil(holiday.date)}
                                                </Typography>
                                            </Box>
                                            <Chip
                                                label={holiday.type}
                                                size="small"
                                                sx={{
                                                    bgcolor: colors.bg,
                                                    color: colors.text,
                                                    fontSize: 8,
                                                    height: 18,
                                                    fontWeight: 500,
                                                    textTransform: 'capitalize'
                                                }}
                                            />
                                        </DashboardListRow>
                                    );
                                })}
                            </Stack>
                        </Box>
                    )}
                </DashboardCard>
            )}

            {/* Events Card */}
            {showEvents && <UpcomingEventsCard events={events} />}
        </Box>
    );
});

QuickInfoCards.displayName = 'QuickInfoCards';

interface QuickInfoCardsSkeletonProps {
    showExams?: boolean;
    showHolidays?: boolean;
    showEvents?: boolean;
}

export const QuickInfoCardsSkeleton: React.FC<QuickInfoCardsSkeletonProps> = memo(({
    showExams = true,
    showHolidays = true,
    showEvents = true
}) => {
    const cardSkeleton = (
        <Box sx={{ flex: 1, display: 'flex' }}>
            <DashboardListCardSkeleton rows={2} rowLead="tile" sx={{ flex: 1 }} />
        </Box>
    );

    return (
        <Box display="flex" gap={2} flexDirection={{ xs: 'column', md: 'row' }}>
            {showExams && cardSkeleton}
            {showHolidays && cardSkeleton}
            {showEvents && cardSkeleton}
        </Box>
    );
});

QuickInfoCardsSkeleton.displayName = 'QuickInfoCardsSkeleton';

export default QuickInfoCards;
